import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ISession } from '../../../models/session';
import { ICertificate } from '../../../models/certificate';
import { TranslateService } from '@ngx-translate/core';
import { CertificatesService } from '../certificates.service';
import { SessionsService } from '../sessions.service';
import { ParticipantsService } from '../participants.service';
import { UsersService } from '../users/users.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EmailService } from '../../email/email.service';
import { IEmail } from '../../../models/email';
import { contains, reduce } from 'ramda';
import { of } from 'rxjs/observable/of';
import { forkJoin } from 'rxjs/observable/forkJoin';

@Component({
  templateUrl: './certificates.component.html',
  styleUrls: ['./certificates.component.scss']
})
export class SessionsCertificatesComponent implements OnInit, OnDestroy {
  public activeTab: string = 'content';

  public session: ISession;
  public certificate: ICertificate;
  public certificateForm: FormGroup;
  public pdfTemplateContent = '';
  public isLoading = false;

  // Participants table
  public participants: any[] = [];
  public filteredParticipants: any[] = [];
  public paginatedParticipants: any[] = [];
  public isLoadingParticipants = false;
  public currentPage = 1;
  public pageSize = 10;
  public pageSizeOptions = [5, 10, 25, 50, 100];
  public totalPages = 1;
  public sortColumn = '';
  public sortDirection: 'asc' | 'desc' = 'asc';
  public searchQuery = '';
  public Math = Math;
  public isDownloadingPDF = false;
  public isSendingEmail = false;
  public isIssuingCertificate = false;
  public selectedParticipant: any = null;
  public showEditModal = false;
  public showConfirmIssueModal = false;
  public participantToIssue: any = null;
  public editForm: FormGroup;

  // Image upload modal
  public showImageModal = false;
  public currentImageField: 'header_logo_image' | 'stamp_image' | 'signature_image' | null = null;
  public selectedImageFile: File | null = null;
  public imagePreview: string | null = null;
  public imageValidationError: string | null = null;

  // Image validation constants
  private readonly ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
  public readonly MAX_FILE_SIZE_KB = 500;
  private readonly MAX_LOGO_HEIGHT = 200;
  private readonly MAX_STAMP_SIGNATURE_HEIGHT = 100;

  public isExportingReportedMinutes = false;
  private participantsReloadInterval: any = null;

  // Batch issue certificates modal
  public showBatchIssueModal = false;
  public issueForAll = false;
  public participantsToIssue: any[] = [];
  public isBatchIssuing = false;
  public batchIssueProgress = 0;
  public batchIssueTotal = 0;

  // Email stats
  public emailStats: { unique_certificates_sent: number; total_certificates_sent: number } | null = null;

  // Batch send email modal
  public showBatchSendEmailModal = false;
  public sendToAll = false;
  public participantsToSendEmail: any[] = [];
  public isBatchSending = false;
  public batchSendProgress = 0;
  public batchSendTotal = 0;

  // Email template
  private certificateEmail: IEmail;
  // public showEmailTemplateModal = false;
  public emailTemplateContent = '';
  public emailSubject = '';
  public emailFrom = '';

  // PDF template modal
  // public showPdfTemplateModal = false;

  public completions: any[] = [];
  private langChange$: any;
  public inserts = [];
  
  // Participant fields tab
  public participantFields = [];
  public participantFieldsUI = [];
  public defaultRegistrationFields = ['FirstName', 'LastName', 'Email'];
  public registrationFieldsRequired = [];
  public registrationFields: any[] = [];
  public paginatedRegistrationFields: any[] = [];
  public isLoadingRegistrationFields = false;
  public fieldsCurrentPage = 1;
  public fieldsPageSize = 10;
  public fieldsTotalPages = 1;
  public showOnlySelectedFields = true;

  constructor(
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private certificatesService: CertificatesService,
    private sessionsService: SessionsService,
    private participantsService: ParticipantsService,
    private usersService: UsersService,
    private emailService: EmailService,
    private router: Router,
    private fb: FormBuilder
  ) {
    this.certificateForm = this.fb.group({
      training_title: ['', Validators.required],
      organization: ['', Validators.required],
      event_date: ['', Validators.required],
      location: ['', Validators.required],
      max_certified_duration_minutes: [0, [Validators.required, Validators.min(0)]],
      min_connection_minutes_for_eligibility: [0, [Validators.required, Validators.min(0)]],
      header_logo_image: [''],
      stamp_image: [''],
      signature_image: [''],
      president_full_name: ['', Validators.required],
      president_title: ['', Validators.required],
      event_page_url: [''],
      questionnaire_url: [''],
    });

    this.editForm = this.fb.group({
      session_id: [null],
      additional_emails: [[]],
      total_connection_time: [null, [Validators.min(0)]],
      certified_duration: [0, [Validators.min(0)]]
    });
  }

  ngOnInit() {
    this.route.parent.data
      .map(route => route.data)
      .subscribe((session: ISession) => {
        this.session = session;
        this.loadCertificate();
        this.loadParticipants();
        this.loadEmailTemplate(session);
        this.loadRegistrationFields(session);
        this.loadCompletions(session);
        this.langChange$ = this.translateService.onLangChange.subscribe(() => {
          this.loadCompletions(this.session);
        });
        this.inserts = this.buildInserts();
        this.participantsReloadInterval = setInterval(() => {
          this.reloadParticipantsInBackground();
        }, 2 * 60 * 1000);
      });
  }

  ngOnDestroy() {
    if (this.participantsReloadInterval) {
      clearInterval(this.participantsReloadInterval);
    }
    if (this.langChange$) {
      this.langChange$.unsubscribe();
    }
  }

  private loadRegistrationFields(session: ISession) {
    this.isLoadingRegistrationFields = true;
    this.usersService.getRegistration(session, true).subscribe(
      fields => {
        this.registrationFields = fields || [];
        this.isLoadingRegistrationFields = false;
        this.registrationFieldsRequired = reduce((acc, field) => {
          return fields.find(f => f.name === field && f.inuse) ? acc : [...acc, field];
        }, [])(this.defaultRegistrationFields);
        this.updateFieldsPagination();
      },
      () => {
        this.isLoadingRegistrationFields = false;
      }
    );
  }

  public getRegistrationFieldLabel(fieldName: string) {
    const field = this.registrationFields.find(f => f.name === fieldName);
    return field ? field.label || fieldName : fieldName;
  }

  updateFieldsPagination() {
    const source = this.showOnlySelectedFields
      ? this.registrationFields.filter(f => f.inuse || this.defaultRegistrationFields.indexOf(f.name) !== -1)
      : this.registrationFields;
    this.fieldsTotalPages = Math.ceil(source.length / this.fieldsPageSize);
    if (this.fieldsCurrentPage > this.fieldsTotalPages) {
      this.fieldsCurrentPage = Math.max(1, this.fieldsTotalPages);
    }
    const start = (this.fieldsCurrentPage - 1) * this.fieldsPageSize;
    this.paginatedRegistrationFields = source.slice(start, start + this.fieldsPageSize);
  }

  onShowOnlySelectedFieldsChange() {
    this.fieldsCurrentPage = 1;
    this.updateFieldsPagination();
  }

  goToFieldsPage(page: number) {
    if (page >= 1 && page <= this.fieldsTotalPages) {
      this.fieldsCurrentPage = page;
      this.updateFieldsPagination();
    }
  }

  get fieldsPages(): number[] {
    const pages = [];
    for (let i = 1; i <= this.fieldsTotalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  isParticipantFieldSelected(fieldName: string): boolean {
    return this.defaultRegistrationFields.indexOf(fieldName) !== -1 || this.participantFields.indexOf(fieldName) !== -1;
  }

  toggleParticipantField(fieldName: string) {
    const idx = this.participantFields.indexOf(fieldName);
    if (idx === -1) {
      this.participantFields = [...this.participantFields, fieldName];
    } else {
      this.participantFields = this.participantFields.filter(f => f !== fieldName);
    }
  }

  private loadCertificate() {
    this.isLoading = true;
    this.certificatesService.get(this.session).subscribe(certificate => {
      this.certificate = certificate;
      this.certificateForm.patchValue({
        training_title: certificate.training_title,
        organization: certificate.organization,
        event_date: certificate.event_date,
        location: certificate.location,
        max_certified_duration_minutes: certificate.max_certified_duration_minutes,
        min_connection_minutes_for_eligibility: certificate.min_connection_minutes_for_eligibility,
        header_logo_image: certificate.header_logo_image || '',
        stamp_image: certificate.stamp_image || '',
        signature_image: certificate.signature_image || '',
        president_full_name: certificate.president_full_name,
        president_title: certificate.president_title,
        event_page_url: certificate.event_page_url,
        questionnaire_url: certificate.questionnaire_url,
      });
      this.pdfTemplateContent = certificate.pdf_template;
      this.participantFields = (certificate.participant_fields as any[]) || this.defaultRegistrationFields;
      this.participantFieldsUI = [...this.participantFields];
      this.isLoading = false;
    });
  }

  private loadParticipants() {
    this.isLoadingParticipants = true;
    this.participantsService.getParticipants(this.session).subscribe(
      participants => {
        this.participants = participants;
        this.applySearchFilter();
        this.isLoadingParticipants = false;
      },
      error => {
        this.isLoadingParticipants = false;
      }
    );
    this.participantsService.getEmailStats(this.session).subscribe(
      stats => {
        this.emailStats = stats;
      },
      () => {}
    );
  }

  private reloadParticipantsInBackground() {
    this.participantsService.getParticipants(this.session).subscribe(
      participants => {
        this.participants = participants;
        this.applySearchFilter();
      },
      () => {}
    );
    this.participantsService.getEmailStats(this.session).subscribe(
      stats => {
        this.emailStats = stats;
      },
      () => {}
    );
  }

  private loadEmailTemplate(session: ISession) {
    this.emailService.getAll(this.session).subscribe(emails => {
      this.certificateEmail = emails.find(email => email.type === 'certificates');
      if (this.certificateEmail) {
        this.emailTemplateContent = this.certificateEmail.content || '';
        this.emailSubject = this.certificateEmail.subject || '';
        this.emailFrom = this.certificateEmail.fromName || '';
      }
    });
  }

  private loadCompletions(session: ISession) {
    this.certificatesService.getTags(session, false).subscribe(tags => {
      const list = tags || [];
      const resolved$ = list.map((t: any) => {
        if (t.label) {
          return of({...t, type: 'session'});
        }
        return this.translateService.get(`tags.raw.${t.tag}`).map((label: string) => ({
          ...t,
          type: 'session',
          label,
        }));
      });
      forkJoin(resolved$).subscribe((completions: any[]) => {
        this.completions = completions;
      });
    });
  }

  private buildInserts() {
    return [
      { name: 'insert-logo', command: 'insertHTML', args: `<img src="${this.certificatesService.getCertificateLogoUrl(this.session)}" style="max-height:${this.MAX_LOGO_HEIGHT}px" />`},
      { name: 'insert-stamp', command: 'insertHTML', args: `<img src="${this.certificatesService.getCertificateStampUrl(this.session)}" style="max-height:${this.MAX_STAMP_SIGNATURE_HEIGHT}px" />`},
      { name: 'insert-signature', command: 'insertHTML', args: `<img src="${this.certificatesService.getCertificateSignatureUrl(this.session)}" style="max-height:${this.MAX_STAMP_SIGNATURE_HEIGHT}px" />`},
      { name: 'insert-hr', command: 'insertHTML', args: '<hr/>'}      
    ]
  }

  private matchParticipant(participant: any, query: string): boolean {
    for (const field of this.participantFieldsUI) {
      if (participant.values[field] && participant.values[field].toLowerCase().includes(query)) {
        return true;
      }
    }
    for (const email of participant.additional_emails || []) {  
      if (email && email.toLowerCase().includes(query)) {
        return true;
      }
    }
    return false;
  }

  applySearchFilter() {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.filteredParticipants = [...this.participants];
    } else {
      const query = this.searchQuery.toLowerCase().trim();
      this.filteredParticipants = this.participants.filter(participant => this.matchParticipant(participant, query));
    }
    this.updatePagination();
  }

  onSearchChange() {
    this.currentPage = 1; // Reset to first page when searching
    this.applySearchFilter();
  }

  clearSearch() {
    this.searchQuery = '';
    this.onSearchChange();
  }

  onPageSizeChange() {
    this.currentPage = 1; // Reset to first page when changing page size
    this.updatePagination();
  }

  sort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    const participantDomesticFields = ['total_connection_time', 'certified_duration'];
    const getValue = (participant: any, column: string) => {
      return contains(column, participantDomesticFields) ? participant[column] : participant.values[column];
    };
    this.filteredParticipants.sort((a, b) => {
      const aValue = getValue(a, column);
      const bValue = getValue(b, column);

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      let comparison = 0;
      if (typeof aValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else {
        comparison = aValue - bValue;
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    this.updatePagination();
  }

  updatePagination() {
    this.totalPages = Math.ceil(this.filteredParticipants.length / this.pageSize);
    if (this.currentPage > this.totalPages) {
      this.currentPage = Math.max(1, this.totalPages);
    }
    this.updatePage();
  }

  updatePage() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedParticipants = this.filteredParticipants.slice(start, end);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePage();
    }
  }

  nextPage() {
    this.goToPage(this.currentPage + 1);
  }

  previousPage() {
    this.goToPage(this.currentPage - 1);
  }

  get pages(): number[] {
    const pages = [];
    for (let i = 1; i <= this.totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  editParticipant(participant: any) {
    this.selectedParticipant = participant;
    this.editForm.patchValue({
      session_id: participant.session_id || +this.session.id,
      additional_emails: (participant.additional_emails || []).join(', '),
      total_connection_time: participant.total_connection_time != null ? participant.total_connection_time : null,
      certified_duration: participant.certified_duration || 0
    });
    this.showEditModal = true;
  }

  saveParticipant() {
    if (this.editForm.invalid || !this.selectedParticipant) {
      return;
    }

    const rawTime = this.editForm.value.total_connection_time;
    const updateData: any = {
      additional_emails: this.editForm.value.additional_emails.split(',; ')
    };
    if (rawTime !== null && rawTime !== '') {
      updateData.total_connection_time = rawTime;
    }
    this.participantsService.updateParticipant(this.selectedParticipant.id, this.session, updateData).subscribe(
      () => {
        this.closeEditModal();
        this.loadParticipants();
      },
      error => {
        console.error('Error updating participant:', error);
      }
    );
  }

  closeEditModal() {
    this.showEditModal = false;
    this.selectedParticipant = null;
    this.editForm.reset();
  }

  resetManualMode() {
    if (!this.selectedParticipant) { return; }
    this.participantsService.resetManualUpdateMode(this.selectedParticipant.id, this.session).subscribe(
      (participant) => {
        this.selectedParticipant = { ...this.selectedParticipant, ...participant };
        this.editForm.patchValue({
          total_connection_time: participant.total_connection_time != null ? participant.total_connection_time : null,
          certified_duration: participant.certified_duration || 0,
        });
      },
      error => console.error('Error resetting manual mode:', error)
    );
  }

  downloadPDF(participant: any) {
    if (!participant.certificate_issued) {
      return;
    }

    this.isDownloadingPDF = true;
    this.certificatesService.downloadCertificate(this.session, participant.id).subscribe(
      result => {
        // Create a download link
        const url = window.URL.createObjectURL(result.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isDownloadingPDF = false;
      },
      error => {
        console.error('Error downloading PDF:', error);
        this.isDownloadingPDF = false;
      }
    );
  }

  getEmailButtonClass(participant: any): object {
    if (!participant.certificate_sent) {
      return { 'btn-white': true };
    }

    const certificateIssued = participant.certificate_issued ? new Date(participant.certificate_issued).getTime() : 0;
    const certificateSent = participant.certificate_sent ? new Date(participant.certificate_sent).getTime() : 0;

    if (certificateSent > certificateIssued) {
      // Email sent after certificate was issued - light green background
      return { 'btn-white': true, 'email-sent-current': true };
    } else if (certificateSent < certificateIssued) {
      // Email sent before certificate was issued (outdated) - light red background
      return { 'btn-white': true, 'email-sent-outdated': true };
    }

    return { 'btn-white': true };
  }

  sendEmail(participant: any) {
    if (!participant.certificate_issued) {
      return;
    }

    this.isSendingEmail = true;
    this.certificatesService.sendEmail(this.session, [participant.id]).subscribe(
      result => {
        console.log('Email sent successfully:', result);
        this.isSendingEmail = false;
        // Reload participants to get updated certificate_sent_status
        this.loadParticipants();
      },
      error => {
        console.error('Error sending email:', error);
        this.isSendingEmail = false;
      }
    );
  }

  issueCertificate(participant: any) {
    if (!participant.eligible_for_certificate) {
      return;
    }

    // If certificate already issued, show confirmation modal
    if (participant.certificate_issued) {
      this.participantToIssue = participant;
      this.showConfirmIssueModal = true;
      return;
    }

    this.proceedWithIssueCertificate(participant);
  }

  proceedWithIssueCertificate(participant: any) {
    this.isIssuingCertificate = true;
    this.certificatesService.generateCertificate(this.session, participant.id).subscribe(
      result => {
        console.log('Certificate issued successfully:', result);
        this.isIssuingCertificate = false;
        // Reload participants to get updated certificate data
        this.loadParticipants();
      },
      error => {
        console.error('Error issuing certificate:', error);
        this.isIssuingCertificate = false;
      }
    );
  }

  confirmIssueCertificate() {
    if (this.participantToIssue) {
      this.proceedWithIssueCertificate(this.participantToIssue);
    }
    this.closeConfirmIssueModal();
  }

  closeConfirmIssueModal() {
    this.showConfirmIssueModal = false;
    this.participantToIssue = null;
  }

  openImageModal(field: 'header_logo_image' | 'stamp_image' | 'signature_image') {
    this.currentImageField = field;
    this.selectedImageFile = null;
    this.imagePreview = this.certificateForm.get(field).value || null;
    this.showImageModal = true;
  }

  closeImageModal() {
    this.showImageModal = false;
    this.currentImageField = null;
    this.selectedImageFile = null;
    this.imagePreview = null;
    this.imageValidationError = null;
  }

  onImageFileSelected(event: any) {
    const file = event.target.files[0];
    this.imageValidationError = null;
    this.imagePreview = null;
    this.selectedImageFile = null;

    if (!file) {
      return;
    }

    // Validate file type
    if (this.ALLOWED_IMAGE_TYPES.indexOf(file.type) === -1) {
      this.imageValidationError = this.translateService.instant('sessions.certificates.image_invalid_type');
      return;
    }

    // Validate file size (500KB max)
    const fileSizeKB = file.size / 1024;
    if (fileSizeKB > this.MAX_FILE_SIZE_KB) {
      this.imageValidationError = this.translateService.instant('sessions.certificates.image_too_large', { maxSize: this.MAX_FILE_SIZE_KB });
      return;
    }

    // Read file and validate dimensions
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const img = new Image();
      img.onload = () => {
        // const maxWidth = this.currentImageField === 'header_logo_image'
        //   ? this.MAX_LOGO_WIDTH
        //   : this.MAX_STAMP_SIGNATURE_WIDTH;

        // if (img.width > maxWidth) {
        //   this.imageValidationError = this.translateService.instant('sessions.certificates.image_too_wide', { maxWidth: maxWidth });
        //   return;
        // }

        // All validations passed
        this.selectedImageFile = file;
        this.imagePreview = e.target.result;
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  saveImage() {
    if (this.imagePreview && this.currentImageField) {
      this.certificateForm.patchValue({
        [this.currentImageField]: this.imagePreview
      });
      this.closeImageModal();
    }
  }

  removeImage(field: 'header_logo_image' | 'stamp_image' | 'signature_image') {
    this.certificateForm.patchValue({
      [field]: ''
    });
  }

  save() {
    if (this.certificateForm.invalid) {
      return;
    }

    this.isLoading = true;
    const formValue = this.certificateForm.value;
    const certificateData: ICertificate = {
      ...formValue,
      session_id: +this.session.id,
      ...(!formValue.questionnaire_url && { questionnaire_url: null }),
      ...(!formValue.event_page_url && { event_page_url: null }),
      pdf_template: this.pdfTemplateContent,
      participant_fields: this.participantFields
    };

    if (this.certificate && this.certificate.id) {
      this.certificatesService.update(this.certificate.id, certificateData).subscribe(
        updatedCertificate => {
          this.certificate = updatedCertificate;
          this.isLoading = false;
          this.participantFieldsUI = [...updatedCertificate.participant_fields as any[]];
          this.sessionsService.setComplete(this.session, 'certificates');
          this.loadParticipants();
        },
        error => {
          this.isLoading = false;
        }
      );
    } else {
      this.certificatesService.create(certificateData).subscribe(
        newCertificate => {
          this.certificate = newCertificate;
          this.isLoading = false;
          this.participantFieldsUI = [...newCertificate.participant_fields as any[]];
          this.sessionsService.setComplete(this.session, 'certificates');
          this.loadParticipants();
        },
        error => {
          this.isLoading = false;
        }
      );
    }
  }

  exportReportedMinutes() {
    this.isExportingReportedMinutes = true;
    this.participantsService.exportReportedMinutes(this.session).subscribe(
      () => {
        this.isExportingReportedMinutes = false;
        this.loadParticipants();
      },
      error => {
        this.isExportingReportedMinutes = false;
      }
    );
  }

  openBatchIssueModal() {
    this.issueForAll = false;
    this.updateParticipantsToIssue();
    this.showBatchIssueModal = true;
  }

  updateParticipantsToIssue() {
    if (this.issueForAll) {
      // All eligible participants
      this.participantsToIssue = this.participants.filter(p => p.eligible_for_certificate);
    } else {
      // Only eligible participants without certificate
      this.participantsToIssue = this.participants.filter(
        p => p.eligible_for_certificate && !p.certificate_issued
      );
    }
  }

  onIssueForAllChange() {
    this.updateParticipantsToIssue();
  }

  closeBatchIssueModal() {
    this.showBatchIssueModal = false;
    this.issueForAll = false;
    this.participantsToIssue = [];
    this.batchIssueProgress = 0;
    this.batchIssueTotal = 0;
  }

  confirmBatchIssue() {
    if (this.participantsToIssue.length === 0) {
      return;
    }

    this.isBatchIssuing = true;
    this.batchIssueTotal = this.participantsToIssue.length;
    this.batchIssueProgress = 0;

    // Process participants sequentially
    this.processBatchIssue(0);
  }

  private processBatchIssue(index: number) {
    if (index >= this.participantsToIssue.length) {
      // All done
      this.isBatchIssuing = false;
      this.loadParticipants();
      this.closeBatchIssueModal();
      return;
    }

    const participant = this.participantsToIssue[index];
    this.certificatesService.generateCertificate(this.session, participant.id).subscribe(
      result => {
        console.log(`Certificate issued for participant ${participant.id}:`, result);
        this.batchIssueProgress = index + 1;
        // Process next participant
        this.processBatchIssue(index + 1);
      },
      error => {
        console.error(`Error issuing certificate for participant ${participant.id}:`, error);
        this.batchIssueProgress = index + 1;
        // Continue with next participant even if this one failed
        this.processBatchIssue(index + 1);
      }
    );
  }

  openBatchSendEmailModal() {
    this.sendToAll = false;
    this.updateParticipantsToSendEmail();
    this.showBatchSendEmailModal = true;
  }

  updateParticipantsToSendEmail() {
    if (this.sendToAll) {
      // All participants with issued certificates
      this.participantsToSendEmail = this.participants.filter(
        p => p.eligible_for_certificate && p.certificate_issued
      );
    } else {
      // Only participants with issued certificates who haven't received email or received outdated email
      this.participantsToSendEmail = this.participants.filter(p => {
        if (!p.eligible_for_certificate || !p.certificate_issued) {
          return false;
        }

        if (!p.certificate_sent) {
          return true; // No email sent yet
        }

        // Check if email is outdated (sent before current certificate)
        const certificateIssued = new Date(p.certificate_issued).getTime();
        const certificateSent = new Date(p.certificate_sent).getTime();
        return certificateSent < certificateIssued;
      });
    }
  }

  onSendToAllChange() {
    this.updateParticipantsToSendEmail();
  }

  closeBatchSendEmailModal() {
    this.showBatchSendEmailModal = false;
    this.sendToAll = false;
    this.participantsToSendEmail = [];
    this.batchSendProgress = 0;
    this.batchSendTotal = 0;
  }

  confirmBatchSendEmail() {
    if (this.participantsToSendEmail.length === 0) {
      return;
    }

    this.isBatchSending = true;
    this.batchSendTotal = this.participantsToSendEmail.length;
    this.batchSendProgress = 0;

    // Process participants sequentially
    this.processBatchSendEmail(0);
  }

  private processBatchSendEmail(index: number) {
    if (index >= this.participantsToSendEmail.length) {
      // All done
      this.isBatchSending = false;
      this.loadParticipants();
      this.closeBatchSendEmailModal();
      return;
    }

    const participant = this.participantsToSendEmail[index];
    this.certificatesService.sendEmail(this.session, [participant.id]).subscribe(
      result => {
        console.log(`Email sent for participant ${participant.id}:`, result);
        this.batchSendProgress = index + 1;
        // Process next participant
        this.processBatchSendEmail(index + 1);
      },
      error => {
        console.error(`Error sending email for participant ${participant.id}:`, error);
        this.batchSendProgress = index + 1;
        // Continue with next participant even if this one failed
        this.processBatchSendEmail(index + 1);
      }
    );
  }

  emailNeverSent(participant: any): boolean {
    return !participant.certificate_sent;
  }

  emailOutdated(participant: any): boolean {
    if (!participant.certificate_sent || !participant.certificate_issued) {
      return false;
    }
    const sentTime = new Date(participant.certificate_sent).getTime();
    const issuedTime = new Date(participant.certificate_issued).getTime();
    return sentTime < issuedTime;
  }

  emailSentCurrent(participant: any): boolean {
    if (!participant.certificate_sent || !participant.certificate_issued) {
      return false;
    }
    const sentTime = new Date(participant.certificate_sent).getTime();
    const issuedTime = new Date(participant.certificate_issued).getTime();
    return sentTime >= issuedTime;
  }

  previewCertificate() {
    this.isLoading = true;
    this.certificatesService.previewCertificate(this.session).subscribe(
      blob => {
        // Open PDF in new window/tab
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        window.URL.revokeObjectURL(url);
        this.isLoading = false;
      },
      error => {
        console.error('Error previewing certificate:', error);
        this.isLoading = false;
      }
    );
  }

  isCertificateSaved(): boolean {
    return this.certificate && this.certificate.id != null;
  }

  saveEmailTemplate() {
    if (this.certificateEmail) {
      const email = {
        ...this.certificateEmail,
        subject: this.emailSubject,
        fromName: this.emailFrom,
        content: this.emailTemplateContent
      }
      this.emailService.saveEmail(this.session, email).subscribe((update: any) => {
        this.certificateEmail = {
          ...this.certificateEmail,
          ...update
        }
        toastr.success(this.translateService.instant('action.success'));
        // this.showEmailTemplateModal = false;
      }, () => {
        toastr.error(this.translateService.instant('action.failed'));
      });
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/certificates/certificates.component.ts